import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';

interface CourseAnalyticsData {
  totalCourses: number;
  publishedCourses: number;
  draftCourses: number;
  totalEnrollments: number;
  averageCompletion: number;
  popularCourses: Array<{
    id: string;
    title: string;
    category: string;
    branch: string;
    enrollments: number;
    completionRate: number;
  }>;
  coursesBySubject: Array<{
    subject: string;
    count: number;
    enrollments: number;
    averageCompletion: number;
  }>;
  certificationStats: Array<{
    certification: string;
    totalIssued: number;
    validCertificates: number;
    expiringThisMonth: number;
  }>;
}

const CourseAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<CourseAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const loadAnalytics = async () => {
      setIsLoading(true);
      
      // Simulate API call
      setTimeout(() => {
        setAnalyticsData({
          totalCourses: 87,
          publishedCourses: 73,
          draftCourses: 14,
          totalEnrollments: 8654,
          averageCompletion: 84.7,
          popularCourses: [
            {
              id: '1',
              title: 'Chứng chỉ An ninh Hàng không Cấp 3',
              category: 'An ninh Hàng không',
              branch: 'Tất cả chi nhánh',
              enrollments: 856,
              completionRate: 92.3,
            },
            {
              id: '2',
              title: 'Kỹ năng Dịch vụ Khách hàng Sân bay',
              category: 'Dịch vụ Khách hàng',
              branch: 'Tân Sơn Nhất - TP.HCM',
              enrollments: 645,
              completionRate: 88.7,
            },
            {
              id: '3',
              title: 'Vận hành Thiết bị Sân bay',
              category: 'Vận hành & Kỹ thuật',
              branch: 'Đà Nẵng',
              enrollments: 512,
              completionRate: 85.1,
            },
            {
              id: '4',
              title: 'IATA Cargo Agent Training',
              category: 'Chứng chỉ Quốc tế',
              branch: 'Cam Ranh - Khánh Hòa',
              enrollments: 423,
              completionRate: 94.8,
            },
            {
              id: '5',
              title: 'Quản lý Chuyến bay và ATC',
              category: 'Quản lý Chuyến bay',
              branch: 'Nội Bài - Hà Nội',
              enrollments: 387,
              completionRate: 89.5,
            },
            {
              id: '6',
              title: 'Xử lý Hàng hóa Nguy hiểm',
              category: 'Hàng hóa & Logistics',
              branch: 'Phú Quốc - Kiên Giang',
              enrollments: 298,
              completionRate: 91.2,
            },
          ],
          coursesBySubject: [
            { 
              subject: 'An ninh Hàng không', 
              count: 18, 
              enrollments: 2156,
              averageCompletion: 91.2
            },
            { 
              subject: 'Dịch vụ Khách hàng', 
              count: 15, 
              enrollments: 1834,
              averageCompletion: 86.7
            },
            { 
              subject: 'Vận hành & Kỹ thuật', 
              count: 22, 
              enrollments: 1567,
              averageCompletion: 83.4
            },
            { 
              subject: 'Chứng chỉ Quốc tế', 
              count: 12, 
              enrollments: 1245,
              averageCompletion: 93.8
            },
            { 
              subject: 'Quản lý Chuyến bay', 
              count: 8, 
              enrollments: 934,
              averageCompletion: 88.9
            },
            { 
              subject: 'Hàng hóa & Logistics', 
              count: 9, 
              enrollments: 712,
              averageCompletion: 89.5
            },
            { 
              subject: 'An toàn Lao động', 
              count: 3, 
              enrollments: 206,
              averageCompletion: 95.1
            },
          ],
          certificationStats: [
            {
              certification: 'Chứng chỉ An ninh Hàng không',
              totalIssued: 1456,
              validCertificates: 1234,
              expiringThisMonth: 87
            },
            {
              certification: 'IATA Cargo Agent',
              totalIssued: 345,
              validCertificates: 312,
              expiringThisMonth: 23
            },
            {
              certification: 'ICAO Air Traffic Management',
              totalIssued: 156,
              validCertificates: 134,
              expiringThisMonth: 12
            },
            {
              certification: 'Dịch vụ Khách hàng Chuyên nghiệp',
              totalIssued: 867,
              validCertificates: 789,
              expiringThisMonth: 45
            },
            {
              certification: 'Vận hành Thiết bị Radar',
              totalIssued: 98,
              validCertificates: 87,
              expiringThisMonth: 8
            }
          ],
        });
        setIsLoading(false);
      }, 1000);
    };

    loadAnalytics();
  }, []);

  if (isLoading) {
    return <div className="w-full py-6 text-center">Đang tải dữ liệu phân tích...</div>;
  }

  if (!analyticsData) {
    return <div className="w-full py-6 text-center">Không có dữ liệu phân tích.</div>;
  }

  // Ensure arrays exist with fallback to empty arrays
  const popularCourses = analyticsData.popularCourses || [];
  const coursesBySubject = analyticsData.coursesBySubject || [];
  const certificationStats = analyticsData.certificationStats || [];

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full px-6 py-6">
        <h1 className="text-2xl font-bold mb-6">Phân tích Khóa học & Chương trình</h1>
        <p className="text-gray-600 mb-6">Hệ thống Dịch vụ Sân bay - Thống kê khóa học đào tạo</p>
        
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 text-blue-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Tổng khóa học</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.totalCourses || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 text-green-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Đã xuất bản</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.publishedCourses || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 text-purple-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Tổng đăng ký</p>
                <p className="text-2xl font-bold text-gray-900">{(analyticsData.totalEnrollments || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 text-yellow-800 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Tỷ lệ hoàn thành TB</p>
                <p className="text-2xl font-bold text-gray-900">{analyticsData.averageCompletion || 0}%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
          {/* Popular Courses */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Khóa học Phổ biến nhất</h2>
            <div className="space-y-4">
              {popularCourses.map((course, index) => (
                <div key={course.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mr-3">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm">{course.title}</h3>
                      <p className="text-xs text-gray-500">{course.category}</p>
                      <p className="text-xs text-blue-600">📍 {course.branch}</p>
                      <p className="text-xs text-gray-400">{course.enrollments} đăng ký</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{course.completionRate}%</p>
                    <p className="text-xs text-gray-500">hoàn thành</p>
                  </div>
                </div>
              ))}
              {popularCourses.length === 0 && (
                <p className="text-gray-500 text-center py-4">Chưa có dữ liệu khóa học</p>
              )}
            </div>
          </div>

          {/* Courses by Subject */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Khóa học theo Chuyên ngành</h2>
            <div className="space-y-4">
              {coursesBySubject.map((subject) => (
                <div key={subject.subject} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">{subject.subject}</h3>
                    <p className="text-xs text-gray-500">{subject.count} khóa học</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{subject.enrollments}</p>
                    <p className="text-xs text-gray-500">đăng ký</p>
                    <p className="text-xs text-green-600">{subject.averageCompletion}% hoàn thành</p>
                  </div>
                </div>
              ))}
              {coursesBySubject.length === 0 && (
                <p className="text-gray-500 text-center py-4">Chưa có dữ liệu chuyên ngành</p>
              )}
            </div>
          </div>
        </div>

        {/* Certification Statistics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Thống kê Chứng chỉ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {certificationStats.map((cert) => (
              <div key={cert.certification} className="text-center p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2 text-sm">{cert.certification}</h3>
                <p className="text-2xl font-bold text-blue-600 mb-1">{cert.totalIssued}</p>
                <p className="text-xs text-gray-500 mb-3">tổng cấp phát</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-green-600">Còn hiệu lực:</span>
                    <span className="font-medium">{cert.validCertificates}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-red-600">Hết hạn tháng này:</span>
                    <span className="font-medium">{cert.expiringThisMonth}</span>
                  </div>
                </div>
              </div>
            ))}
            {certificationStats.length === 0 && (
              <div className="col-span-full text-center py-4">
                <p className="text-gray-500">Chưa có dữ liệu chứng chỉ</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseAnalytics; 